<script lang="ts">
  // Timer / Transport — the design-system transport primitive
  // (wiki/feature-design-system.md §Component primitives + §Typography, AC4).
  //
  // Composes the recording footer: the tabular timer (Be Vietnam Pro 700, 40px) with
  // its red record dot + goal caption, an animated waveform, and the pause/stop
  // controls. The record dot is ALWAYS the semantic recording-red token,
  // regardless of the practice accent or theme. The waveform bars animate while
  // recording; both honour prefers-reduced-motion.
  //
  // Controls are passed as typed callbacks, never slots. The host owns the
  // recording state machine; this primitive renders + fires.
  import Button from '$lib/ui/primitives/Button.svelte';
  import { Pause, Square } from '$lib/ui/icons';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  interface Props {
    /** The elapsed-time display, e.g. "02:47". */
    time: string;
    /** The goal caption under the timer, e.g. "of 05:00 target". */
    goal?: string | null;
    /** The status kicker label; omit for the localized "recording". */
    statusLabel?: string | null;
    /** Whether the take is actively recording (drives waveform animation). */
    recording?: boolean;
    /** Reduced-motion — freezes the waveform/dot. */
    reducedMotion?: boolean;
    /** Pause handler — omit to hide the pause button. */
    onPause?: (() => void) | null;
    /** Stop handler — omit to hide the stop button. */
    onStop?: (() => void) | null;
    /** Pause button label; omit for the localized "pause". The icon is the component's. */
    pauseLabel?: string | null;
    /** Stop button label; omit for the localized "stop". The icon is the component's. */
    stopLabel?: string | null;
    class?: string;
  }

  let {
    time,
    goal = null,
    statusLabel = null,
    recording = true,
    reducedMotion = false,
    onPause = null,
    onStop = null,
    pauseLabel = null,
    stopLabel = null,
    class: klass = '',
  }: Props = $props();

  // 18 waveform bars with the reference's staggered delays.
  const barDelays = [
    0, 0.12, 0.28, 0.06, 0.34, 0.18, 0.42, 0.22, 0.36, 0.1, 0.46, 0.04, 0.3,
    0.16, 0.4, 0.24, 0.08, 0.32,
  ];
  const still = $derived(reducedMotion || !recording);
</script>

<section class="vd-transport {klass}" class:vd-transport--still={still}>
  <div class="vd-transport__tw">
    <span class="vd-transport__status">
      <span class="vd-transport__rec" aria-hidden="true"></span>
      {statusLabel ?? t(UiKeys.Transport.StatusRecording)}
    </span>
    <span class="vd-transport__time" aria-label={t(UiKeys.Transport.Elapsed, { Time: time })}
      >{time}</span
    >
    {#if goal}<span class="vd-transport__goal">{goal}</span>{/if}
  </div>

  <div class="vd-transport__wf" aria-hidden="true">
    {#each barDelays as delay, i (i)}
      <i style="animation-delay: {delay}s"></i>
    {/each}
  </div>

  <div class="vd-transport__acts">
    {#if onPause}
      <Button variant="ghost" icon={Pause} onclick={onPause}
        >{pauseLabel ?? t(UiKeys.Transport.Pause)}</Button
      >
    {/if}
    {#if onStop}
      <Button variant="primary" icon={Square} onclick={onStop}
        >{stopLabel ?? t(UiKeys.Transport.Stop)}</Button
      >
    {/if}
  </div>
</section>

<style lang="scss">
  .vd-transport {
    padding-top: 22px;
    border-top: 1px solid var(--line);
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 26px;
    align-items: center;
  }

  .vd-transport__tw {
    display: flex;
    flex-direction: column;
  }

  .vd-transport__status {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--accent-text);
    margin-bottom: 4px;
  }

  // The record dot is ALWAYS the semantic recording-red (theme-independent).
  .vd-transport__rec {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-recording);
    animation: vd-rec-blink 1.2s infinite;
  }

  @keyframes vd-rec-blink {
    50% {
      opacity: 0.35;
    }
  }

  .vd-transport__time {
    font-family: var(--font-ui);
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    font-size: var(--text-timer);
    line-height: 0.95;
    letter-spacing: -0.01em;
    color: var(--ink);
  }

  .vd-transport__goal {
    font-family: var(--font-ui);
    font-size: var(--text-meta);
    font-weight: 700;
    color: var(--ink-mute);
    letter-spacing: 0.04em;
    margin-top: 4px;
  }

  .vd-transport__wf {
    display: flex;
    align-items: center;
    gap: 2.5px;
    height: 38px;

    i {
      width: 2.5px;
      background: var(--accent);
      opacity: 0.55;
      border-radius: 2px;
      animation: vd-wf-bounce 1.1s ease-in-out infinite;
    }
  }

  @keyframes vd-wf-bounce {
    0%,
    100% {
      height: 20%;
    }
    50% {
      height: 90%;
    }
  }

  .vd-transport__acts {
    display: flex;
    gap: 10px;
  }

  // Reduced motion / not-recording — freeze the dot + bars at a mid level.
  .vd-transport--still {
    .vd-transport__rec {
      animation: none;
    }
    .vd-transport__wf i {
      animation: none;
      height: 45%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .vd-transport__rec,
    .vd-transport__wf i {
      animation: none;
    }
    .vd-transport__wf i {
      height: 45%;
    }
  }

  @media (max-width: 560px) {
    .vd-transport {
      grid-template-columns: 1fr;
      gap: 18px;
    }
    .vd-transport__wf {
      display: none;
    }
  }
</style>
